import React from 'react'
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack
} from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { FiArrowRight, FiCheck, FiShoppingBag, FiTrendingUp, FiZap } from 'react-icons/fi'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'

export const ForProductsPage: React.FC = () => {
  const navigate = useNavigate()

  const handleGetStarted = () => navigate('/for-products/get-started')
  const handleHowItWorks = () => navigate('/how-it-works')

  return (
    <Box bg="white" minH="100vh" display="flex" flexDirection="column">
      <SpotlightHeader />

      <Box flex={1}>
        {/* Hero */}
        <Box
          bgGradient="linear(to-br, white, purple.50, brand.50)"
          pt={{ base: 20, md: 32 }}
          pb={{ base: 16, md: 24 }}
          position="relative"
          overflow="hidden"
        >
          <Box
            position="absolute"
            top="-10%"
            right="-5%"
            w="800px"
            h="800px"
            borderRadius="full"
            bgGradient="radial(purple.100, transparent)"
            opacity={0.4}
            filter="blur(80px)"
          />
          <Box
            position="absolute"
            bottom="-10%"
            left="-5%"
            w="600px"
            h="600px"
            borderRadius="full"
            bgGradient="radial(brand.100, transparent)"
            opacity={0.35}
            filter="blur(80px)"
          />

          <Container maxW="7xl" position="relative" zIndex={1}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={16} alignItems="center">
              <VStack align="start" gap={8} textAlign="left">
                <Badge
                  colorPalette="purple"
                  size="lg"
                  px={4}
                  py={2}
                  borderRadius="full"
                  variant="subtle"
                  border="1px solid"
                  borderColor="purple.200"
                >
                  For Wellness Products
                </Badge>

                <VStack align="start" gap={6}>
                  <Heading
                    size={{ base: '3xl', md: '4xl', lg: '5xl' }}
                    color="gray.900"
                    lineHeight="1.1"
                    fontWeight="extrabold"
                    letterSpacing="tight"
                  >
                    Sell products with story-based ads and a page that converts.
                  </Heading>

                  <Text fontSize={{ base: 'xl', md: '2xl' }} color="gray.600" lineHeight="tall" maxW="xl">
                    We generate story-first carousel ads and a matching landing page from a few inputs—then you refine in minutes.
                    No ad managers. No funnel builder.
                  </Text>

                  <Text fontSize="md" color="gray.500" fontStyle="italic">
                    “For outcome-driven product brands that create real change.”
                  </Text>
                </VStack>

                <HStack gap={4} flexWrap="wrap" pt={2}>
                  <Button
                    colorPalette="brand"
                    size="xl"
                    fontWeight="bold"
                    px={10}
                    h={16}
                    fontSize="lg"
                    onClick={handleGetStarted}
                    _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                    transition="all 0.2s"
                  >
                    <HStack gap={3}>
                      <Text>Start Building</Text>
                      <Icon as={FiArrowRight} />
                    </HStack>
                  </Button>
                  <Button
                    variant="ghost"
                    size="xl"
                    fontWeight="semibold"
                    px={8}
                    h={16}
                    fontSize="lg"
                    onClick={handleHowItWorks}
                    color="gray.600"
                    _hover={{ bg: 'gray.50', color: 'brand.700' }}
                  >
                    See How It Works
                  </Button>
                </HStack>

                <HStack gap={8} pt={8} borderTop="1px solid" borderColor="gray.100" w="full">
                  <VStack align="start" gap={0}>
                    <Text fontSize="2xl" fontWeight="bold" color="gray.900">Story</Text>
                    <Text fontSize="sm" color="gray.500">Carousel Templates</Text>
                  </VStack>
                  <VStack align="start" gap={0}>
                    <Text fontSize="2xl" fontWeight="bold" color="gray.900">Match</Text>
                    <Text fontSize="sm" color="gray.500">Ad → Landing Page</Text>
                  </VStack>
                  <VStack align="start" gap={0}>
                    <Text fontSize="2xl" fontWeight="bold" color="gray.900">Fast</Text>
                    <Text fontSize="sm" color="gray.500">Preview in Minutes</Text>
                  </VStack>
                </HStack>
              </VStack>

              {/* Right side: lightweight “what you get” card stack */}
              <VStack align="stretch" gap={4} display={{ base: 'none', lg: 'flex' }}>
                {[
                  {
                    icon: FiShoppingBag,
                    title: 'Product-first inputs',
                    description: 'Tell us who it’s for, the change it creates, and add an image—then we build from there.'
                  },
                  {
                    icon: FiZap,
                    title: 'Story carousel candidates',
                    description: '3 story-based carousels (4–7 slides) generated instantly using our templates.'
                  },
                  {
                    icon: FiTrendingUp,
                    title: 'A landing page that matches',
                    description: 'A simple page designed to convert the exact promise your carousel makes.'
                  }
                ].map((item) => (
                  <Box
                    key={item.title}
                    bg="white"
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor="gray.100"
                    shadow="sm"
                    p={6}
                  >
                    <HStack align="start" gap={4}>
                      <Box p={3} bg="purple.50" borderRadius="xl" color="purple.700">
                        <Icon as={item.icon} boxSize={6} />
                      </Box>
                      <VStack align="start" gap={1}>
                        <Heading size="md" color="gray.900" fontWeight="bold">
                          {item.title}
                        </Heading>
                        <Text color="gray.600" lineHeight="tall">
                          {item.description}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                ))}
              </VStack>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Benefits */}
        <Box bg="gray.50" py={{ base: 16, md: 24 }}>
          <Container maxW="7xl">
            <VStack align="stretch" gap={12}>
              <Box textAlign="center" maxW="3xl" mx="auto">
                <Badge colorPalette="green" size="lg" mb={4} variant="subtle">
                  Why this works
                </Badge>
                <Heading size={{ base: '2xl', md: '3xl' }} color="gray.900" mb={4} letterSpacing="tight">
                  A clear story converts better than a crowded ad.
                </Heading>
                <Text fontSize="xl" color="gray.600">
                  Our carousels deliver one message in simple beats: who it’s for → the before → the after → the next step.
                </Text>
              </Box>

              <SimpleGrid columns={{ base: 1, md: 3 }} gap={8}>
                {[
                  {
                    title: 'Lower cognitive load',
                    description: 'Buyers swipe through a single idea instead of decoding a dense graphic.',
                    icon: FiCheck
                  },
                  {
                    title: 'Stronger intent',
                    description: 'Story structure helps the right person self-identify quickly.',
                    icon: FiCheck
                  },
                  {
                    title: 'Better message match',
                    description: 'The landing page mirrors the carousel promise to reduce bounce.',
                    icon: FiCheck
                  }
                ].map((item) => (
                  <Box
                    key={item.title}
                    bg="white"
                    p={8}
                    borderRadius="2xl"
                    border="1px solid"
                    borderColor="gray.100"
                    shadow="sm"
                  >
                    <HStack gap={3} mb={3} color="brand.700">
                      <Icon as={item.icon} />
                      <Heading size="md" color="gray.900">
                        {item.title}
                      </Heading>
                    </HStack>
                    <Text color="gray.600" lineHeight="tall">
                      {item.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>

              <Flex justify="center" pt={4}>
                <Button
                  size="xl"
                  colorPalette="brand"
                  onClick={handleGetStarted}
                  px={12}
                  fontSize="lg"
                  fontWeight="bold"
                >
                  Start Building
                </Button>
              </Flex>
            </VStack>
          </Container>
        </Box>
      </Box>

      {/* Mobile Sticky CTA */}
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        bg="white"
        p={4}
        borderTopWidth="1px"
        borderColor="gray.200"
        shadow="xl"
        display={{ base: 'block', md: 'none' }}
        zIndex={1000}
      >
        <Button w="full" colorPalette="brand" size="lg" onClick={handleGetStarted} fontWeight="bold">
          <HStack gap={2}>
            <Text>Start Building</Text>
            <Icon as={FiArrowRight} />
          </HStack>
        </Button>
      </Box>

      <SpotlightFooter />
    </Box>
  )
}

export default ForProductsPage


